import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, LinearProgress, Stack, Typography, alpha } from '@mui/material';
import { getAiAccessMe } from '../../helpers/api';

const formatResetTime = (resetsAtUtc) =>
  resetsAtUtc
    ? new Date(resetsAtUtc).toLocaleString('en-GB', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';

/**
 * Loads GET /AiAccess/Me, which returns one status per feature: { assistant, aiReport }.
 * feature: 'assistant' (Ask AI) or 'aiReport' (AI-written reports).
 * If the check itself fails (e.g. the feature isn't set up yet), access stays null and AI is
 * treated as available, matching the backend's behaviour.
 */
export function useAiAccess(feature = 'assistant') {
  const [access, setAccess] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const overview = await getAiAccessMe();
      // Older responses were a single status rather than one per feature
      setAccess(overview?.[feature] ?? (overview && 'allowed' in overview ? overview : null));
    } catch (error) {
      setAccess(null);
    }
  }, [feature]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // FACILITY_REQUIRED isn't a block: it just means a facility must be picked
  const blocked = Boolean(
    access && access.allowed === false && access.reasonCode !== 'FACILITY_REQUIRED'
  );
  return { access, blocked, refresh, setAccess };
}

// Where a feature's daily limit comes from (limitSource from /AiAccess/Me)
const LIMIT_SOURCE_TEXT = {
  USER: 'Limit set for your account',
  FACILITY: 'Limit set for your facility',
  CATEGORY: 'Limit set for your user category',
  GLOBAL: 'Default limit for everyone',
};

function UsageBar({ label, note, remaining, limit, resetsAtUtc, color }) {
  const usedPct = Math.min(100, ((limit - remaining) / limit) * 100);
  const low = remaining <= Math.max(1, Math.ceil(limit * 0.2));
  const barColor = low ? '#ed6c02' : color;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="caption" fontWeight={600} color={low ? '#b35400' : 'text.secondary'}>
          {label}
        </Typography>
        {resetsAtUtc && (
          <Typography variant="caption" color="text.disabled" noWrap>
            Resets {formatResetTime(resetsAtUtc)}
          </Typography>
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={usedPct}
        sx={{
          mt: 0.5,
          height: 6,
          borderRadius: 3,
          bgcolor: alpha(barColor, 0.15),
          '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 3 },
        }}
      />
      {note && (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

/**
 * What's left today, or the reason AI is unavailable.
 * facility: optional facility allowance (Ask AI for consultants/personnel), whose limit is
 * shared by everyone at that facility.
 * unit: what is being counted, e.g. 'AI reports' or 'questions'.
 */
export default function AiAccessStatus({
  access,
  facility = null,
  unit = 'requests',
  color = '#0c5bab',
  sx,
}) {
  if (!access) return null;

  if (access.allowed === false && access.reasonCode !== 'FACILITY_REQUIRED') {
    const isLimit = access.reasonCode === 'LIMIT_REACHED';
    return (
      <Alert severity={isLimit ? 'warning' : 'info'} sx={{ borderRadius: '12px', ...sx }}>
        {access.reason ||
          (isLimit ? "You've used all your AI requests for today." : 'AI is not available for your account.')}
        {isLimit && access.resetsAtUtc && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Your requests reset {formatResetTime(access.resetsAtUtc)}.
          </Typography>
        )}
      </Alert>
    );
  }

  const bars = [];

  if (facility) {
    // Ask AI for consultants/personnel: the facility's shared allowance
    if (facility.enabled === false) {
      return (
        <Alert severity="info" sx={{ borderRadius: '12px', ...sx }}>
          Ask AI is turned off for {facility.facilityName}.
        </Alert>
      );
    }
    if (facility.dailyLimit != null) {
      const remaining =
        facility.remaining ?? Math.max(facility.dailyLimit - (facility.usedToday || 0), 0);
      bars.push(
        <UsageBar
          key="facility"
          label={`${facility.dailyLimit - remaining} of ${facility.dailyLimit} questions used today at ${facility.facilityName}`}
          remaining={remaining}
          limit={facility.dailyLimit}
          resetsAtUtc={access.resetsAtUtc}
          color={color}
        />
      );
    }
  } else if (access.dailyLimit != null) {
    const remaining = access.remaining ?? Math.max(access.dailyLimit - (access.usedToday || 0), 0);
    bars.push(
      <UsageBar
        key="own"
        label={`${remaining} of ${access.dailyLimit} ${unit} left today`}
        note={LIMIT_SOURCE_TEXT[access.limitSource]}
        remaining={remaining}
        limit={access.dailyLimit}
        resetsAtUtc={access.resetsAtUtc}
        color={color}
      />
    );
  }

  // Combined allowance across both AI features
  if (access.totalDailyLimit != null) {
    const remaining =
      access.totalRemaining ?? Math.max(access.totalDailyLimit - (access.totalUsedToday || 0), 0);
    bars.push(
      <UsageBar
        key="total"
        label={`${remaining} AI request${remaining === 1 ? '' : 's'} left today in total`}
        remaining={remaining}
        limit={access.totalDailyLimit}
        resetsAtUtc={bars.length ? null : access.resetsAtUtc}
        color={color}
      />
    );
  }

  if (!bars.length) return null; // unlimited: nothing to show

  return (
    <Stack spacing={1.25} sx={{ ...sx }}>
      {bars}
    </Stack>
  );
}
